import LobModelExtenstion from 'integration/quoteandbind/directives/LobOfferingSelection/util/LobOfferingHelper';
import _ from 'lodash';
import appFeatures from 'effective-features!';


/**
 * This allows the UI to render issues in the offering -> uwIssues map with the order given by the relevantOfferings
 * E.g. with the given uwIssuesByOffering
 * {
 *   'CUSTOM': [uwIssue1, uwIssue2],
 *   'Premium' [uwIssue1, uwIssue2]
 *   'Base': [uwIssue2],
 *   'Standard' [uwIssue1],
 * }
 *
 * if the relevantOfferings are as sorted as follows
 * ['Base', 'Standard', 'Premium'],
 *
 * then, when iterating over the entries of the resulting object, we would get an order of iteration defined
 * by the one defined in relevantOffering
 *
 * @param {Object} uwIssuesByOffering the map offering -> uwIssues
 * @param {string[]} relevantOfferings a sorted array of offering names in which they should appear in the UI
 * @returns {Object} a new map offering -> uwIssues that will render in the UI with the requested order
 */
function recomposeObjectUsingOfferedQuotesOrder(uwIssuesByOffering, relevantOfferings) {

    const offeringNameIndex = _.fromPairs(relevantOfferings.map((offeringName, index) => ([offeringName, index])));
    const sortPairByOfferingIndex = ([offeringName1/*, rest */], [offeringName2/*, rest */]) => offeringNameIndex[offeringName1] - offeringNameIndex[offeringName2];

    const offeringIssuesPairs = _.toPairs(uwIssuesByOffering)
        .sort(sortPairByOfferingIndex);

    return _.fromPairs(offeringIssuesPairs);
}

export default function ($filter, $scope, CoveragesUtil, UnderwritingIssuesUtil, FlowValidationService, FLOW_UNDERWRITING_ISSUE) {
    const $ctrl = this;
    const emptySchedules = [];

    const lobCode = $ctrl.lobCode();
    Object.assign($ctrl.offeringUtilFunctions.getLobData(), LobModelExtenstion.getLobExtension(lobCode));
    const coverageHelper = buildCoverageHelper(lobCode);
    coverageHelper.updateModels();
    const relevantOfferings = coverageHelper.getOfferingHeaders().map(offering => offering.branchName);

    function setupUWIssues(uwIssues) {
        const displayableUwIssues = UnderwritingIssuesUtil.addDisplayKeys(uwIssues);
        $ctrl.blockedQuotes = UnderwritingIssuesUtil.getBlockedQuotes(displayableUwIssues);
        const uwIssuesByOffering = UnderwritingIssuesUtil.getUwIssuesByOffering(displayableUwIssues);
        $ctrl.uwIssuesByOffering = recomposeObjectUsingOfferedQuotesOrder(uwIssuesByOffering, relevantOfferings);
    }

    $scope.$watch(() => FlowValidationService.getDisplayableErrors({types: [FLOW_UNDERWRITING_ISSUE]}), setupUWIssues, true);


    $ctrl.updateModels = () => coverageHelper.updateModels();
    $ctrl.getBaseCoverageData = () => coverageHelper.getBaseCoverageData();
    $ctrl.getLobCoverageData = () => coverageHelper.getLobCoverageData();
    $ctrl.getOfferingHeaders = () => coverageHelper.getOfferingHeaders().filter(offering => offering.branchName != 'Version #1');
    $ctrl.getSchedulesModel = () => {
        if (coverageHelper.getSchedulesModel) {
            return coverageHelper.getSchedulesModel();
        }
        return emptySchedules;
    };
    $ctrl.doesAnyOfferingHaveBlockingUWIssues = doesAnyOfferingHaveBlockingUWIssues;
    $ctrl.getOfferingsWithUWIssues = getOfferingsWithUWIssues;
    $ctrl.uwIssueText = setUWIssueText();
    $ctrl.shouldDisplayPriceDifferenceFor = shouldDisplayPriceDifferenceFor;
    $ctrl.getPriceDiffFor = getPriceDiffFor;

    $ctrl.showUwIssueDetails = appFeatures.gatewayquoteandbind;

    $ctrl.recalculate = (forms, offering) => {
        // check each form and ensure all are valid
        const formsValid = !_.find(forms, form => !CoveragesUtil.validateForm(form));

        if (formsValid) {
            $ctrl.offeringUtilFunctions.activateOffering(offering.branchCode, $ctrl.updateModels);
        }
    };

    function buildCoverageHelper(lob) {
        const CoverageHelperConstructor = LobModelExtenstion.getCoverageHelperConstructor(lob);
        return new CoverageHelperConstructor($filter, $ctrl.offeringUtilFunctions);
    }
    //INCI11490369 typo error ALFA ROMEO
    $ctrl.replaceCertified = function(coverageName){
        console.log("coverageName "+coverageName.replace(/Certfied/g, "Certified"));
        return coverageName.replace(/Certfied/g, "Certified");
    }
    /**
     * Returns the appropriate display key to the user depending on how many UW Issues are present
     * @returns {String}
     * @private
     */
    function setUWIssueText() {
        const offeringsWithUWIssues = $ctrl.getOfferingsWithUWIssues();

        let uwIssueWarningText;
        if (offeringsWithUWIssues.length === 1) {
            uwIssueWarningText = $filter('translate')('quoteandbind.pa.directives.MultiQuoteView.UW Issue For 1 Offering', {
                offeringName: offeringsWithUWIssues[0].branchName
            });
        } else if (offeringsWithUWIssues.length === 2) {
            uwIssueWarningText = $filter('translate')('quoteandbind.pa.directives.MultiQuoteView.UW Issue For 2 Offerings', {
                offeringName1: offeringsWithUWIssues[0].branchName,
                offeringName2: offeringsWithUWIssues[1].branchName
            });
        } else if (offeringsWithUWIssues.length === 3) {
            uwIssueWarningText = $filter('translate')('quoteandbind.pa.directives.MultiQuoteView.UW Issue For 3 Offerings', {
                offeringName1: offeringsWithUWIssues[0].branchName,
                offeringName2: offeringsWithUWIssues[1].branchName,
                offeringName3: offeringsWithUWIssues[2].branchName
            });
        } else {
            uwIssueWarningText = $filter('translate')('quoteandbind.pa.directives.MultiQuoteView.UW Issue for n Offerings', {
                offeringCount: offeringsWithUWIssues.length
            });
        }
        return uwIssueWarningText;
    }

    /**
     * Determine whether any offerings have blocking Underwriting issues
     * @param {Array} offerings - array of quote offerings
     * @returns {Boolean}
     */
    function doesAnyOfferingHaveBlockingUWIssues(offerings) {
        if (FlowValidationService.isActive()) {
            const offeringsNames = offerings.map(offering => offering.branchName);
            const uwIssues = FlowValidationService.getDisplayableErrors({types: [FLOW_UNDERWRITING_ISSUE]})
                .filter(uwIssue => _.includes(offeringsNames, uwIssue.offering));
            return UnderwritingIssuesUtil.getBlockedQuotes(uwIssues).length > 0;
        }
        return _.some(offerings, (offering) => offering.hasBlockingUWIssues);
    }

    /**
     * Returns all non CUSTOM offerings that have underwriting issues
     * @returns {Array} offerings
     * @private
     */
    function getOfferingsWithUWIssues() {
    //UCWPRODENG-830 revert
        return $ctrl.offeringUtilFunctions.getOfferedQuotes().filter(offering => offering.hasBlockingUWIssues && offering.branchName !== 'CUSTOM');
    }

    /**
     * Wrapper function to make it more readable
     * @param {string} offeringCode
     * @returns {boolean}
     */
    function shouldDisplayPriceDifferenceFor(offeringCode) {
        return $ctrl.offeringUtilFunctions.displayPriceDifference(offeringCode);
    }

    /**
     * Wrapper function to make it more readable
     * @param {string} offeringCode
     * @returns {string}
     */
    function getPriceDiffFor(offeringCode) {
        return $ctrl.offeringUtilFunctions.getFormattedPriceDifference(offeringCode);
    }
}
